/* import __COLOCATED_TEMPLATE__ from './content-part.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type TaggablePart from 'embercom/objects/inbox/taggable-part';
import { isTaggable } from 'embercom/objects/inbox/taggable-part';
import type Tag from 'embercom/objects/inbox/tag';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import type Conversation from 'embercom/objects/inbox/conversation';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { taskFor } from 'ember-concurrency-ts';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import { PartGroupCategory } from 'embercom/objects/inbox/conversation-stream/part-group';
import type Session from 'embercom/services/session';
import type TracingService from 'embercom/services/tracing';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type ConversationUpdates from 'embercom/services/conversation-updates';
import type IntlService from 'embercom/services/intl';
import { type Block } from '@intercom/interblocks.ts';
import { translatedPartExists } from './helpers/translations';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';

interface Args {
  classStyling?: string;
  conversation?: Conversation;
  part: RenderablePart & { renderableData: { blocks?: Block[] } };
  index: number;
  partGroup: PartGroup;
  isReadOnly: boolean;
  fullWidth?: boolean;
  showOriginalMessage?: boolean;
  toggleTranslation?: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
  Blocks: {
    content: [];
    channel?: [];
    seenState?: [];
    sendState?: [];
    timestamp?: [];
  };
}

export default class Inbox2ConversationStreamContentPartComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare tracing: TracingService;
  @service declare intercomEventService: any;
  @service declare session: Session;
  @service declare conversationUpdates: ConversationUpdates;
  @service declare intl: IntlService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @tracked isHovered = false;
  @tracked isSending = false;
  partGroupCategories = PartGroupCategory;

  get isSideConversation() {
    return this.args.conversation?.isSideConversation;
  }

  get classStyling() {
    let defaultStyles = 'relative inbox2__renderable-part inbox2__break-words py-3 px-4';
    let styles = this.args.classStyling || defaultStyles;
    return [this.bubbleRounding, styles].join(' ');
  }

  get showTranslationOption() {
    return (
      this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
        this.args.conversation,
      ) && translatedPartExists(this.args.partGroup)
    );
  }

  get bubbleRounding(): string {
    let bubbleRoundings = this.args.partGroup.isUser ? ['rounded-r-3xl'] : ['rounded-l-3xl'];
    if (this.args.partGroup.parts.length === 1) {
      bubbleRoundings.push(this.args.partGroup.isUser ? 'rounded-l-3xl' : 'rounded-r-3xl');
    } else if (this.args.index + 1 === this.args.partGroup.parts.length) {
      bubbleRoundings.push(this.args.partGroup.isUser ? 'rounded-bl-3xl' : 'rounded-br-3xl');
    } else if (this.args.index === 0) {
      bubbleRoundings.push(this.args.partGroup.isUser ? 'rounded-tl-3xl' : 'rounded-tr-3xl');
    }
    return bubbleRoundings.join(' ');
  }

  get isTaggablePart() {
    return isTaggable(this.args.part);
  }

  get permalinkId() {
    if (this.args.conversation) {
      return this.args.part.generatePermalinkId(this.args.conversation.id);
    }
    return '';
  }

  get isNote() {
    return this.args.part.renderableType === RenderableType.AdminNote;
  }

  get isPhoneCall() {
    return this.args.part.isPhoneCallPart;
  }

  get conversationPartBackgroundStyles() {
    let renderablePart = this.args.part.renderableData;
    let hoverStyles = !this.failedToSend && renderablePart.styles?.backgroundColorHover;

    if (this.isHovered && hoverStyles) {
      return hoverStyles;
    }

    return renderablePart.styles?.backgroundColor;
  }

  get existingUpdate() {
    return (
      this.args.conversation &&
      this.conversationUpdates.existingUpdate(this.args.part, this.args.conversation)
    );
  }

  get failedToSend() {
    return this.existingUpdate?.isFailed;
  }

  get isEmail() {
    return (
      this.args.partGroup.category === this.partGroupCategories.TeammateEmailReply ||
      this.args.partGroup.category === this.partGroupCategories.BotEmailReply
    );
  }

  get isUserContent() {
    return this.args.partGroup.category === this.partGroupCategories.UserContent;
  }

  @action removeTag(tagToRemove: Tag) {
    this.optimisticRemoveTag(tagToRemove);
  }

  @action legacyRemoveTag(tagToRemove: Tag) {
    if (this.args.conversation && this.isTaggablePart) {
      let renderablePart = this.args.part as TaggablePart;
      let tagsWithoutRemovedTag = renderablePart.renderableData.tags.reject(
        (tag) => tag === tagToRemove,
      );
      taskFor(this.inboxState.updateTags).perform(
        this.args.conversation,
        renderablePart,
        tagsWithoutRemovedTag,
      );
      this.trackTagRemovedEvent(this.args.conversation?.id);
    }
  }

  @action optimisticRemoveTag(tagToRemove: Tag) {
    if (this.args.conversation && this.isTaggablePart) {
      let renderablePart = this.args.part as TaggablePart;
      this.inboxState.removeTag(this.args.conversation, renderablePart, tagToRemove);
    }
  }

  private trackTagRemovedEvent(conversationId: number) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'tag_removed',
      object: 'conversation_part',
      place: 'inbox',
      inbox_type: this.inboxState.activeInbox?.type,
      conversation_id: conversationId,
    });
  }

  @action instrumentImages(element: HTMLElement) {
    let images = element.getElementsByTagName('img');

    for (let i = 0; i < images.length; i++) {
      this.tracing.trackImageLoad(images[i]);
    }
  }

  @action
  handleCopy(event: ClipboardEvent) {
    if (!this.session.workspace.isFeatureEnabled('team-messenger-custom-emojis')) {
      return;
    }

    let selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    let range = selection.getRangeAt(0);
    let tempDiv = document.createElement('div');
    let linkElement = null;
    try {
      let commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.TEXT_NODE && commonAncestor.parentElement?.closest) {
        linkElement = commonAncestor.parentElement.closest('a');
      } else if ((commonAncestor as Element).closest) {
        linkElement = (commonAncestor as Element).closest('a');
      }
    } catch (error) {
      linkElement = null;
    }

    if (linkElement) {
      let clonedLink = linkElement.cloneNode(false);
      clonedLink.appendChild(range.cloneContents());
      tempDiv.appendChild(clonedLink);
    } else {
      tempDiv.appendChild(range.cloneContents());
    }

    // Handle custom emojis replacement
    let customEmojiImages = tempDiv.querySelectorAll('img.custom-emoji');
    customEmojiImages.forEach((img) => {
      let emojiName = img.getAttribute('data-custom-emoji');
      if (emojiName) {
        let textNode = document.createTextNode(emojiName);
        img.parentNode?.replaceChild(textNode, img);
      }
    });

    // Preserve HTML structure for hyperlinks and other formatting
    let finalHtml = tempDiv.innerHTML;
    let finalText = tempDiv.textContent || '';

    finalText = finalText
      .replace(/\n\s+\n/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\n{3,}/g, '\n\n');

    event.clipboardData?.setData('text/html', finalHtml);
    event.clipboardData?.setData('text/plain', finalText);
    event.preventDefault();
  }

  @action async retrySendMessage() {
    let { conversation, part } = this.args;

    if (!conversation) {
      return;
    }

    if (this.existingUpdate) {
      this.conversationUpdates.rollbackUpdates(conversation.id, [this.existingUpdate]);
    }

    this.isSending = true;

    try {
      await this.inboxState.retrySendReplyOrNote({
        conversation,
        part,
        isNote: this.isNote,
      });
    } finally {
      this.isSending = false;
    }
  }

  get isFinAnswer() {
    return this.args.part.renderableType === RenderableType.FinAnswer;
  }

  get uploadsWithoutAudioDuplicates() {
    let renderableData = this.args.part.renderableData as any;
    let uploads = renderableData.uploads;
    if (!uploads?.length) {
      return [];
    }

    let blocks = this.args.part.renderableData.blocks;
    let hasAudioFileBlock = blocks?.some((block: Block) => block.type === 'audioFile');
    if (!hasAudioFileBlock) {
      return uploads;
    }

    return uploads.filter((upload: any) => {
      let name = upload.name?.toLowerCase() ?? '';
      return !name.endsWith('.ogg') && !name.endsWith('.oga') && !name.endsWith('.webm');
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::ContentPart': typeof Inbox2ConversationStreamContentPartComponent;
    'inbox2/conversation-stream/content-part': typeof Inbox2ConversationStreamContentPartComponent;
  }
}
